/**
 * Centralized route protection configuration
 * Maps routes to required permissions
 */

import type { User } from "@/models/user";
import { EQUIPMENT_PERMISSIONS, Permission, PermissionGroups } from "@/types/permissions";
import { hasAnyRole, isTenantAdmin } from "@/utils/permissions";

import { PATH } from "./router/routerConfig";

export const AGENT_RUNTIME_ADMIN_ROLES = [
  "admin",
  "administrator",
  "tenant_admin",
  "platform_admin",
  "runtime_operator"
] as const;

export const RAG_REVIEWER_ROLES = [
  ...AGENT_RUNTIME_ADMIN_ROLES,
  "incident_responder",
  "security_auditor",
  "manager",
  "reviewer",
  "release_operator"
] as const;

export const RAG_REVIEWER_PERMISSIONS = [
  ...PermissionGroups.KNOWLEDGE_PACKS,
  Permission.AGENT_PLATFORM_ADMIN,
  Permission.AGENT_PLATFORM_RUNTIME,
  Permission.TENANT
] as const;

export const RAG_SOURCE_CONTROL_PERMISSIONS = [Permission.KNOWLEDGE_PACK_MANAGE] as const;

export const hasRagReviewAccess = (user: User | undefined, permissions: string[]): boolean => {
  const workspaceRole = String(user?.workspace_role || "")
    .trim()
    .toLowerCase();
  return (
    Boolean(user?.is_staff) ||
    isTenantAdmin(user) ||
    workspaceRole === "owner" ||
    workspaceRole === "admin" ||
    hasAnyRole(user, [...RAG_REVIEWER_ROLES]) ||
    RAG_REVIEWER_PERMISSIONS.some((permission) => permissions.includes(permission))
  );
};

/** Source selection changes are a write operation, separate from RAG review access. */
export const hasRagSourceControlAccess = (user: User | undefined, permissions: string[]): boolean => {
  const workspaceRole = String(user?.workspace_role || "")
    .trim()
    .toLowerCase();
  return (
    Boolean(user?.is_staff) ||
    isTenantAdmin(user) ||
    workspaceRole === "owner" ||
    workspaceRole === "admin" ||
    hasAnyRole(user, [...AGENT_RUNTIME_ADMIN_ROLES]) ||
    RAG_SOURCE_CONTROL_PERMISSIONS.some((permission) => permissions.includes(permission))
  );
};

export interface RoutePermission {
  path: string;
  permissions: string[];
  roles?: string[];
  tenantKinds?: string[];
  requireAll?: boolean;
  exact?: boolean; // If true, only match exact path. If false, match path prefix
}

/**
 * Define all protected routes and their required permissions
 * This is the single source of truth for route permissions
 */
export const PROTECTED_ROUTES: RoutePermission[] = [
  // Product handoff routes are authenticated by the Worksine shell and
  // capability-checked again by the target service after redemption.
  { path: "/llmgw", permissions: [], exact: false },
  { path: "/edge/helpdesk", permissions: [], exact: false },

  // Sales Routes
  // Work is a shared landing section: its child entries remain independently
  // protected, but support-only users must be able to open the section shell.
  {
    path: "/sales",
    permissions: [
      Permission.SALES_ACTIVITY,
      Permission.SALES_LEAD,
      Permission.SUPPORT_CASE_VIEW,
      Permission.SUPPORT_CASE_MANAGE
    ],
    exact: false
  },
  { path: "/sales/pipeline", permissions: [Permission.SALES_ACTIVITY], exact: false },
  {
    path: "/sales/support-pipeline",
    permissions: [Permission.SUPPORT_CASE_VIEW, Permission.SUPPORT_CASE_MANAGE],
    exact: false
  },
  { path: "/sales/open-activities", permissions: [Permission.SALES_ACTIVITY], exact: false },
  { path: "/sales/activities", permissions: [Permission.SALES_ACTIVITY], exact: false },
  { path: "/sales/change-review", permissions: [Permission.CRM_CHANGE_HISTORY], exact: false },
  { path: "/sales/quota", permissions: [Permission.QUOTA], exact: false },
  { path: "/sales/escalations", permissions: [Permission.SALES_ACTIVITY], exact: false },
  { path: "/sales/news", permissions: [Permission.SALES_ACTIVITY], exact: false },
  { path: "/sales/leads", permissions: [Permission.SALES_LEAD], exact: false },

  // Email intelligence is a shared inbound workspace surface. Tenant
  // administrators need read navigation after configuring a mailbox; detailed
  // visibility and mutations remain enforced by each backend BFF action.
  {
    path: PATH.EMAIL_INTELLIGENCE,
    permissions: [
      Permission.SALES_ACTIVITY,
      Permission.SUPPORT_CASE_VIEW,
      Permission.SUPPORT_CASE_MANAGE,
      Permission.TENANT
    ],
    exact: false
  },

  // Marketing/CRM Routes
  { path: "/marketing/customer", permissions: [Permission.SALES_CUSTOMER], exact: false },
  { path: "/marketing/campaign", permissions: [Permission.SALES_CAMPAIGN], exact: false },
  { path: "/marketing/product", permissions: [Permission.SALES_PRODUCT], exact: false },

  // Opportunity (uses same protection as pipeline)
  { path: "/opportunity", permissions: [Permission.SALES_ACTIVITY], exact: false },

  // Equipment operations
  { path: PATH.EQUIPMENT, permissions: [EQUIPMENT_PERMISSIONS.view, EQUIPMENT_PERMISSIONS.manage], exact: false },
  {
    path: PATH.MARKETING_ASSETS,
    permissions: [EQUIPMENT_PERMISSIONS.view, EQUIPMENT_PERMISSIONS.manage],
    exact: false
  },
  { path: PATH.BOM_BUILDER, permissions: [EQUIPMENT_PERMISSIONS.view, EQUIPMENT_PERMISSIONS.manage], exact: false },

  // Agent Routes
  {
    path: "/agents/settings",
    permissions: [Permission.AGENT_PLATFORM_ADMIN],
    roles: ["incident_responder", "runtime_operator", "security_auditor"],
    exact: false
  },
  {
    path: "/agents/runtime",
    // User-view access is separate from backend-authorized workspace runtime
    // access. The roles below mirror user_can_view_admin_runtime().
    permissions: [
      Permission.TASKS,
      Permission.TRIAGE_SUB_AGENT,
      Permission.AGENT_PLATFORM_ADMIN,
      Permission.TENANT,
      Permission.AGENT_PLATFORM_RUNTIME
    ],
    roles: [...AGENT_RUNTIME_ADMIN_ROLES],
    exact: false
  },
  {
    path: "/agents/network-os-validation",
    permissions: [Permission.AGENT_PLATFORM_RUNTIME],
    roles: [...AGENT_RUNTIME_ADMIN_ROLES],
    exact: false
  },
  {
    path: "/agents/network-automation",
    permissions: [Permission.AGENT_PLATFORM_RUNTIME],
    roles: [...AGENT_RUNTIME_ADMIN_ROLES],
    exact: false
  },
  { path: "/agents", permissions: [Permission.TRIAGE_SUB_AGENT, Permission.AGENT_PLATFORM_ADMIN], exact: false },
  { path: "/agents/tool", permissions: [Permission.AGENT_TOOLS], exact: false },
  { path: "/tasks", permissions: [Permission.TASKS], exact: false },

  // Company OS Routes
  { path: "/company/collective/agents", permissions: [...PermissionGroups.COMPANY_OS_ADMINISTRATION], exact: false },
  { path: "/company/collective", permissions: [...PermissionGroups.COMPANY_OS_MANAGEMENT], exact: false },
  { path: "/company/people", permissions: [...PermissionGroups.COMPANY_OS_MANAGEMENT], exact: false },
  { path: "/company/signal", permissions: [...PermissionGroups.COMPANY_OS], exact: false },
  { path: "/company/triage", permissions: [...PermissionGroups.COMPANY_OS], exact: false },
  { path: "/company/deliverables", permissions: [...PermissionGroups.COMPANY_OS], exact: false },
  { path: "/company", permissions: [...PermissionGroups.COMPANY_OS], exact: false },

  // Operator Routes
  { path: "/ops", permissions: [...PermissionGroups.OPS], tenantKinds: ["ops"], exact: false },

  // Settings Routes
  { path: PATH.TENANT_MANAGEMENT, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_WORK_SALES_PIPELINE, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_WORK_SUPPORT_PIPELINE, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_WORK_LEAD_PIPELINE, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_WORK_OUTCOME_PACKS, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_WORKSPACE_OVERVIEW, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_WORKSPACE_SERVICE_ACCOUNTS, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_AI_MODELS, permissions: [Permission.TENANT], exact: true },
  { path: PATH.SETTINGS_AI_MODELS_PROVIDERS, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_AI_MODELS_RUNTIME, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_AI_MODELS_MODEL_MAPPING, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_AI_MODELS_MODEL_OVERRIDES, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_AI_MODELS_BUDGET_ALERTS, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_ACCESS_CONTROL_DASHBOARD, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_APPS, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_APPS_HELPDESK_POLICY, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_APPS_HELPDESK_OPERATIONS, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_APPS_EMAIL_VENDOR_PROFILES, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_APPS_EMAIL_AUTOMATION_POLICY, permissions: [Permission.TENANT], exact: false },
  { path: PATH.SETTINGS_AGENTS_SKILLS_STORE, permissions: [Permission.TENANT], exact: false },
  { path: "/setting/system/users", permissions: [Permission.GROUP], exact: false },
  { path: "/setting/system/groups", permissions: [Permission.GROUP], exact: false },
  { path: "/setting/system/permissions", permissions: [Permission.PERMISSION], exact: false },
  { path: "/setting/system/policies", permissions: [Permission.GROUP], exact: false },
  { path: "/setting/audit", permissions: [Permission.AUDIT], exact: false },
  { path: "/setting/crm", permissions: [Permission.APP_INTEGRATIONS], exact: false },
  { path: "/setting/system-integrations", permissions: [Permission.APP_INTEGRATIONS], exact: false },
  { path: "/setting/knowledge-packs", permissions: [...PermissionGroups.KNOWLEDGE_PACKS], exact: false },
  { path: "/data/knowledgemgr", permissions: [], exact: false },
  { path: PATH.OFFICE_GLOSSARY, permissions: [], exact: false },
  {
    path: "/setting/rag",
    permissions: [...RAG_REVIEWER_PERMISSIONS],
    roles: [...RAG_REVIEWER_ROLES],
    exact: false
  },
  // Dynamic workspace-admin/reviewer/release grants are enforced by Backend.
  // This entry makes the route authenticated without replacing those grants
  // with an unrelated static browser permission.
  { path: "/setting/knowledge-models", permissions: [], exact: false },
  { path: "/setting/knowledgemgr", permissions: [], exact: false },
  { path: PATH.TRAINING, permissions: [], exact: false },
  { path: "/setting/tenants", permissions: [Permission.TENANT], exact: false }
];

const normalizeRoutePath = (pathname: string): string => String(pathname || "").split(/[?#]/, 1)[0] || "/";

const matchesRoutePath = (pathname: string, route: RoutePermission): boolean => {
  const currentPath = normalizeRoutePath(pathname);
  if (route.exact) {
    return currentPath === route.path;
  }
  return currentPath === route.path || currentPath.startsWith(`${route.path}/`);
};

/**
 * Get required permissions for a given route
 * @param pathname - The route pathname to check
 * @returns RoutePermission object if route is protected, null otherwise
 */
export function getRoutePermissions(pathname: string): RoutePermission | null {
  // Find the most specific matching route
  const matches = PROTECTED_ROUTES.filter((route) => matchesRoutePath(pathname, route));

  // Return the longest matching path (most specific)
  if (matches.length === 0) return null;

  return matches.reduce((prev, current) => {
    return current.path.length > prev.path.length ? current : prev;
  });
}

/**
 * Check if a route requires authentication
 * @param pathname - The route pathname to check
 * @returns true if route is protected
 */
export function isProtectedRoute(pathname: string): boolean {
  return getRoutePermissions(pathname) !== null;
}
